import { ArrowRight, Info } from "lucide-react";
import { InkCap, InkBook, InkBriefcase, InkAward } from "./InkIcons";
import { useLang } from "../lib/lang";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";

const ICONS = { ug: InkCap, pg: InkBook, executive: InkBriefcase, diploma: InkAward };
const SPANS = ["md:col-span-3", "md:col-span-3", "md:col-span-3", "md:col-span-3"];

const ProgramCard = ({ p, i, label, onEnquire }) => {
  const Icon = ICONS[p.key];
  const featured = i === 0 || i === 3;
  return (
    <Reveal delay={i * 0.06} className={`${SPANS[i]} h-full`}>
      <article data-testid={`program-card-${p.key}`}
        className={`group relative flex h-full flex-col overflow-hidden rounded-[1.75rem] p-7 transition-[box-shadow,transform] duration-500 hover:-translate-y-1 sm:p-9 ${featured ? "bg-ink text-white hover:shadow-[0_30px_60px_-30px_rgba(30,43,107,0.9)]" : "border border-ink/10 bg-white text-night hover:shadow-[0_30px_60px_-34px_rgba(18,24,63,0.5)]"}`}>
        <div className={`absolute -right-16 -top-16 h-48 w-48 rounded-full transition-transform duration-700 group-hover:scale-125 ${featured ? "bg-sun/90" : "bg-sky"}`} aria-hidden="true" />
        <span className={`relative flex h-16 w-16 items-center justify-center rounded-[1.1rem_1.5rem_1rem_1.6rem] text-white transition-transform duration-500 group-hover:rotate-0 ${i % 2 ? "rotate-3" : "-rotate-3"} ${featured ? "bg-white/10" : "bg-ink"}`}>
          <Icon className="h-9 w-9" />
        </span>
        <span className={`relative mt-2 self-end font-display text-xs font-bold ${featured ? "text-white/70" : "text-ink/60"}`} aria-hidden="true">0{i + 1}</span>
        <h3 className={`relative mt-auto pt-10 font-display text-2xl font-extrabold sm:text-3xl ${featured ? "text-white" : "text-ink"}`}>{p.title}</h3>
        <p className={`relative mt-2 text-base ${featured ? "text-white/80" : "text-night/75"}`}>{p.text}</p>
        <button onClick={() => onEnquire(p.value)} data-testid={`program-enquire-${p.key}`}
          className={`relative mt-7 inline-flex w-fit items-center gap-2 rounded-full font-display text-sm font-extrabold transition-[gap] duration-300 hover:gap-3.5 ${featured ? "text-sun" : "text-ink underline decoration-sun decoration-2 underline-offset-8"}`}>
          {label} <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </article>
    </Reveal>
  );
};

export const Programs = ({ onEnquire }) => {
  const { t } = useLang();
  return (
    <section id="programs" className="scroll-mt-20 bg-white py-24 lg:py-32" data-testid="programs-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <Reveal><Eyebrow>{t.programs.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.05}><SectionTitle>{t.programs.title}</SectionTitle></Reveal>
        </div>
        <div className="mt-14 grid gap-5 md:grid-cols-6">
          {t.programs.items.map((p, i) => <ProgramCard key={p.key} p={p} i={i} label={t.programs.enquire} onEnquire={onEnquire} />)}
        </div>
        <p className="mt-8 max-w-3xl text-base leading-relaxed text-night" data-testid="programs-mode-note">{t.programs.note}</p>
        <p className="mt-4 flex max-w-3xl items-start gap-3 rounded-2xl bg-sky/70 p-4 text-sm leading-relaxed text-night" data-testid="programs-excluded-note"><Info className="mt-0.5 h-4 w-4 shrink-0 text-ink" aria-hidden="true" />{t.programs.excluded}</p>
      </div>
    </section>
  );
};

export const Marquee = () => {
  const { t } = useLang();
  const row = [...t.marquee, ...t.marquee];
  return (
    <div className="overflow-hidden border-y border-ink/10 bg-mist py-8" aria-hidden="true" data-testid="editorial-marquee">
      <div className="marquee-track flex w-max items-center">
        {[0, 1].map((k) => (
          <div key={k} className="flex shrink-0 items-center">
            {row.map((m, i) => (
              <span key={`${k}-${i}`} className="flex items-center font-display text-3xl font-extrabold tracking-tight text-ink sm:text-5xl">
                <span className={i % 2 ? "text-outline" : ""}>{m}</span>
                <span className="mx-8 inline-block h-4 w-4 rounded-full bg-sun sm:mx-12" />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

